import { MESSAGE_ACTION, MESSAGE_TYPE } from 'consts/enum';
import { generate6DigitUUID, throttle } from 'helpers/helpers';
import { ENGT_CHAT } from 'js/bot.model';
import { typingIndicatorOptions } from './typingIndicator.model';
import './typingIndicator.scss';

export class TypingIndicatorModule {
	constructor(props) {
		this.initData(props);
		this.initWrapper();
		this.initEvent();
	}

	initData(propData = {}) {
		this.data = typingIndicatorOptions;
		this.setter(propData);
		this.data.id = propData.id || `message-${generate6DigitUUID()}`;
	}

	initWrapper() {
		const { id } = this.data;
		const wrapper = document.createElement('div');
		const textTemplateForIndicator = '<strong>Typing...</strong>';
		wrapper.id = id;
		wrapper.className = 'engt-hidden';
		wrapper.innerHTML = textTemplateForIndicator;

		this.html = wrapper;
	}

	initEvent() {
		ENGT_CHAT.set('showTypingIndicator', throttle(showTypingIndicator, 300));
		ENGT_CHAT.set('addTypingIndicatorElement', addTypingIndicatorElement);
		ENGT_CHAT.set('removeTypingIndicatorElement', removeTypingIndicatorElement);
	}

	setter(propData) {
		this.data = {
			...this.data,
			...propData,
		};
	}

	render() {
		return this.html;
	}
}

const addTypingIndicatorElement = (id) => {
	const typingIndicator = document.getElementById(id);
	const chatContentWrapper = document.getElementById('engtChatContent');
	if (typingIndicator) {
		chatContentWrapper.style.paddingBottom = '24px';
		typingIndicator.classList.remove('engt-hidden');
		typingIndicator.classList.add(
			'engt-show-typing-indicator',
			'engt-typing-indicator',
			'engt-typing-indicator-bottom-up'
		);
	}
};

const removeTypingIndicatorElement = (id) => {
	const typingIndicator = document.getElementById(id);
	if (typingIndicator) {
		typingIndicator.classList.remove(
			'engt-show-typing-indicator',
			'engt-typing-indicator',
			'engt-typing-indicator-bottom-up'
		);
		typingIndicator.classList.add('engt-hidden');
	}
};

const createTypingIndicatorElement = (id, name) => {
	const typingIndicator = document.getElementById(id);
	if (!typingIndicator) return null;

	typingIndicator.innerHTML = '';

	const wrapper = document.createElement('div');
	wrapper.className = 'engt-thinking-wrapper';

	const strongEl = document.createElement('strong');
	strongEl.innerText = name;

	const statusEl = document.createElement('span');
	statusEl.className = 'engt-ripple-text';
	statusEl.innerText = ENGT_CHAT.get('GENERIC_STRINGS')?.TYPING || 'is typing';

	const dotsEl = document.createElement('div');
	dotsEl.className = 'engt-typing-dots';
	dotsEl.innerHTML = '<span></span><span></span><span></span>';

	wrapper.appendChild(strongEl);
	wrapper.appendChild(document.createTextNode(' '));
	wrapper.appendChild(statusEl);
	wrapper.appendChild(dotsEl);

	typingIndicator.appendChild(wrapper);

	return { statusEl, wrapper };
};

let typingState = {
	thinkingTimeout: null,
	pendingTyping: false,
	statusEl: null,
	isStreaming: false,
};

const clearTypingIndicator = (id) => {
	removeTypingIndicatorElement(id);
	if (typingState.thinkingTimeout) clearTimeout(typingState.thinkingTimeout);
	typingState = {
		thinkingTimeout: null,
		pendingTyping: false,
		statusEl: null,
		isStreaming: false,
	};
};

const resetTypingTimeout = (id) => {
	if (ENGT_CHAT.get('clearTimeoutTypingOff')) clearTimeout(ENGT_CHAT.get('clearTimeoutTypingOff'));
	ENGT_CHAT.set(
		'clearTimeoutTypingOff',
		setTimeout(() => {
			clearTypingIndicator(id);
		}, 7000)
	);
};

const handleTypingOrThinking = (message, id, name) => {
	if (message['action'] === MESSAGE_ACTION.THINKING) {
		if (!typingState.statusEl) {
			const els = createTypingIndicatorElement(id, name);
			typingState.statusEl = els.statusEl;
		}
		typingState.statusEl.innerText = ENGT_CHAT.get('GENERIC_STRINGS')?.THINKING || 'is thinking';

		typingState.pendingTyping = false;
		typingState.isStreaming = true;
		if (typingState.thinkingTimeout) clearTimeout(typingState.thinkingTimeout);

		typingState.thinkingTimeout = setTimeout(() => {
			if (typingState.pendingTyping && typingState.statusEl) {
				typingState.statusEl.innerText = ENGT_CHAT.get('GENERIC_STRINGS')?.TYPING || 'is typing';
				typingState.pendingTyping = false;
				typingState.thinkingTimeout = null;
			} else {
				typingState.thinkingTimeout = null;
			}
		}, 700);

		addTypingIndicatorElement(id);
		resetTypingTimeout(id);
	} else if (message['action'] === MESSAGE_ACTION.TYPING_ON) {
		if (typingState.thinkingTimeout) {
			typingState.pendingTyping = true;
			typingState.isStreaming = true;
		} else {
			if (!typingState.statusEl) {
				const els = createTypingIndicatorElement(id, name);
				typingState.statusEl = els.statusEl;
			}
			typingState.statusEl.innerText = ENGT_CHAT.get('GENERIC_STRINGS')?.TYPING || 'is typing';
		}

		addTypingIndicatorElement(id);
		resetTypingTimeout(id);
	} else {
		clearTypingIndicator(id);
	}
};

const handleLLMStreaming = () => {
	typingState.isStreaming = true;
	resetTypingTimeout();
};

const handleOtherMessages = (msg_type, isRudValidationPacket, id) => {
	typingState.isStreaming = false;
	if (msg_type !== MESSAGE_TYPE.ASK_USER_NAME && !isRudValidationPacket) {
		clearTypingIndicator(id);
	}
};

const showTypingIndicator = (data, id) => {
	const message = data['message'];
	const msg_type = message['type'];
	const botName = ENGT_CHAT.get('config.bot_name');
	const name = data['agent_name'] || botName;
	const isRudValidationPacket = Boolean(data?.validated) || false;

	const typingIndicator = document.getElementById(id);
	if (!typingIndicator) return;

	if ([MESSAGE_TYPE.TYPING_INDICATOR, MESSAGE_TYPE.THINKING_INDICATOR].includes(msg_type)) {
		handleTypingOrThinking(message, id, name);
	} else if (message?.data?.answer_type === MESSAGE_TYPE.LLM_STREAM) {
		handleLLMStreaming();
	} else {
		handleOtherMessages(msg_type, isRudValidationPacket, id);
	}
};
